import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff, ClipboardCheck, Shield, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

export default function ExaminerLoginPage() {
  const { login, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });

  if (isAuthenticated && user?.role === 'examiner') {
    navigate('/examiner');
    return null;
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(loginForm.email, loginForm.password);
      const userRole = result.user?.role || 'candidate';
      if (userRole === 'examiner' || userRole === 'admin') {
        toast.success('Welcome, Examiner');
        navigate('/examiner');
      } else {
        toast.error('Access denied. This portal is for examiners only.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center bg-gradient-to-br from-[#1B2A4A] to-[#2d3f5e] py-16" data-testid="examiner-login-page">
      <div className="w-full max-w-md px-4">
        <Link to="/" className="inline-flex items-center text-white/70 hover:text-white mb-6 text-sm">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to Home
        </Link>
        
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-[#C5A572] mx-auto mb-4 flex items-center justify-center">
            <ClipboardCheck className="h-8 w-8 text-[#1B2A4A]" />
          </div>
          <h1 className="text-2xl font-bold text-white font-heading">Examiner Portal</h1>
          <p className="text-sm text-slate-300 mt-1">IMCAC Examination Assessment System</p>
        </div>

        <Card className="imcac-card border-0 shadow-2xl">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-6 p-3 bg-amber-50 border border-amber-200 rounded-sm">
              <Shield className="h-5 w-5 text-amber-600" />
              <p className="text-xs text-amber-800">Authorized examiners only. Access credentials are provided by IMCAC administration.</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="examiner-email" className="text-sm font-medium text-slate-700">Examiner Email</Label>
                <Input
                  id="examiner-email"
                  type="email"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                  placeholder="examiner@imcac.org"
                  required
                  className="mt-1 rounded-sm"
                  data-testid="examiner-login-email"
                />
              </div>
              <div>
                <Label htmlFor="examiner-password" className="text-sm font-medium text-slate-700">Password</Label>
                <div className="relative mt-1">
                  <Input
                    id="examiner-password"
                    type={showPassword ? 'text' : 'password'}
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    placeholder="••••••••"
                    required
                    className="rounded-sm pr-10"
                    data-testid="examiner-login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                className="w-full bg-[#1B2A4A] hover:bg-[#2d3f5e] text-white rounded-sm"
                disabled={loading}
                data-testid="examiner-login-submit"
              >
                {loading ? 'Signing in...' : 'Access Examiner Dashboard'}
              </Button>
            </form>

            <div className="mt-6 pt-4 border-t border-slate-200">
              <p className="text-xs text-slate-500 text-center">
                Need examiner credentials? Contact{' '}
                <a href="mailto:admin@imcac.org" className="text-[#C5A572] hover:underline">admin@imcac.org</a>
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="mt-6 text-center">
          <p className="text-xs text-slate-400">
            Other portals:{' '}
            <Link to="/login" className="text-[#C5A572] hover:underline">Candidate</Link>
            {' • '}
            <Link to="/login/institutional" className="text-[#C5A572] hover:underline">Institution</Link>
            {' • '}
            <Link to="/login/examination-center" className="text-[#C5A572] hover:underline">Exam Center</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
